import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "proposal_sent", "won", "lost"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter]);

  async function updateLead(id, body) {
    try {
      await api.patch(`/admin/leads/${id}`, body);
      toast.success("Lead updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function deleteLead(id) {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="admin-leads">
      <Header title="Leads" sub="Incoming requests from the public site and AI planner.">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-[200px]" data-testid="leads-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </Header>

      {loading ? (
        <div className="mt-8 text-neutral-500">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="mt-8 surface p-10 text-center text-neutral-500" data-testid="leads-empty">
          No leads yet. They&apos;ll appear here as the public site receives enquiries.
        </div>
      ) : (
        <div className="mt-8 surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10">
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-neutral-500">
                <th className="p-4">Lead</th>
                <th className="p-4">Source</th>
                <th className="p-4">Message</th>
                <th className="p-4">Status</th>
                <th className="p-4">Created</th>
                <th className="p-4 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4 align-top">
                    <div className="font-medium">{l.name}</div>
                    <div className="text-xs text-neutral-400 flex items-center gap-1 mt-0.5"><Mail size={11} /> {l.email}</div>
                    {l.phone && <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><Phone size={11} /> {l.phone}</div>}
                    {l.company && <div className="text-xs text-neutral-500 mt-0.5">{l.company}</div>}
                  </td>
                  <td className="p-4 align-top">
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-white/5 border border-white/10 px-2 py-1">{l.source}</span>
                  </td>
                  <td className="p-4 align-top max-w-md">
                    <div className="text-neutral-300 text-sm line-clamp-4">{l.message}</div>
                  </td>
                  <td className="p-4 align-top">
                    <select
                      value={l.status}
                      onChange={(e) => updateLead(l.id, { status: e.target.value })}
                      data-testid={`lead-status-${l.id}`}
                      className="max-w-[160px]"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="p-4 align-top text-xs text-neutral-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 align-top">
                    <button onClick={() => deleteLead(l.id)} className="text-neutral-500 hover:text-red-400" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Header({ title, sub, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
      <div>
        <div className="overline">Console · {title}</div>
        <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">{title}</h1>
        {sub && <p className="mt-1 text-neutral-400 text-sm">{sub}</p>}
      </div>
      {children && <div>{children}</div>}
    </div>
  );
}
